import { DestroyRef, Injectable, InjectionToken, inject, signal } from '@angular/core';

import { minWidthQuery } from '../constants/breakpoints.constant';

/** Rango de pantalla de E-011 RN-1. */
export type Viewport = 'mobile' | 'medium' | 'large';

export type MatchMediaFn = (query: string) => MediaQueryList;

/**
 * `window.matchMedia` inyectable, para poder falsearlo en specs sin depender
 * del tamaño real de la ventana. `null` cuando el entorno no lo soporta.
 */
export const MATCH_MEDIA = new InjectionToken<MatchMediaFn | null>('MATCH_MEDIA', {
  providedIn: 'root',
  factory: () =>
    typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia.bind(window) : null,
});

/**
 * Rango de pantalla actual (`mobile` < 768 ≤ `medium` < 1280 ≤ `large`) como
 * signal de solo lectura, actualizado en caliente al cruzar un breakpoint.
 * Sin `matchMedia` disponible asume `large` (comportamiento de escritorio).
 */
@Injectable({ providedIn: 'root' })
export class ViewportService {
  private readonly matchMedia = inject(MATCH_MEDIA);

  private readonly mediumQuery = this.matchMedia?.(minWidthQuery('md')) ?? null;
  private readonly largeQuery = this.matchMedia?.(minWidthQuery('xl')) ?? null;

  private readonly current = signal<Viewport>(this.resolve());
  readonly viewport = this.current.asReadonly();

  constructor() {
    const onChange = (): void => this.current.set(this.resolve());
    const queries = [this.mediumQuery, this.largeQuery].filter((query): query is MediaQueryList => !!query);

    queries.forEach((query) => query.addEventListener('change', onChange));
    inject(DestroyRef).onDestroy(() =>
      queries.forEach((query) => query.removeEventListener('change', onChange)),
    );
  }

  private resolve(): Viewport {
    if (!this.mediumQuery || !this.largeQuery) return 'large';
    if (this.largeQuery.matches) return 'large';
    return this.mediumQuery.matches ? 'medium' : 'mobile';
  }
}
